import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import marker2x from "leaflet/dist/images/marker-icon-2x.png";
import marker from "leaflet/dist/images/marker-icon.png";
import shadow from "leaflet/dist/images/marker-shadow.png";
import { Building2, Crosshair, Navigation, Phone } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: marker2x, iconUrl: marker, shadowUrl: shadow });

const userIcon = L.divIcon({ className: "user-dot", html: "<span></span>", iconSize: [18, 18], iconAnchor: [9, 9] });
const distKm = (a, b, c, d) => { const r = Math.PI / 180; const h = Math.sin((c - a) * r / 2) ** 2 + Math.cos(a * r) * Math.cos(c * r) * Math.sin((d - b) * r / 2) ** 2; return 6371.0088 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)); };

const Recenter = ({ position }) => { const map = useMap(); useEffect(() => { if (position) map.setView(position, 13); }, [map, position]); return null; };

export default function HospitalMap() {
  const [hospitals, setHospitals] = useState([]);
  const [position, setPosition] = useState(null);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/hospitals`).then(r => setHospitals(r.data)).catch(() => setError("Data rumah sakit gagal dimuat. Coba lagi sebentar lagi.")); }, []);

  const locate = () => {
    if (!navigator.geolocation) return setError("Perangkat ini tidak mendukung deteksi lokasi.");
    setLocating(true); setError("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => { setPosition([coords.latitude, coords.longitude]); setLocating(false); },
      () => { setError("Izin lokasi ditolak. Aktifkan lokasi lalu coba lagi, atau lihat daftar rumah sakit di bawah."); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const sorted = useMemo(() => {
    if (!position) return hospitals;
    return [...hospitals].map(h => ({ ...h, distance: h.lat != null ? distKm(position[0], position[1], h.lat, h.lon) : null })).sort((a, b) => (a.distance ?? 1e9) - (b.distance ?? 1e9));
  }, [hospitals, position]);

  const first = sorted.find(h => h.lat != null);
  const center = position || (first ? [first.lat, first.lon] : [-6.2, 106.816]);

  return <div className="map-wrap" data-testid="hospital-map-screen">
    <div className="map-hero">
      <p className="eyebrow">BANTUAN MEDIS</p>
      <h1>Rumah sakit<br /><em>terdekat dari Anda.</em></h1>
      <p className="lead">Aktifkan lokasi agar daftar diurutkan dari yang paling dekat dengan posisi korban.</p>
      <button className="primary-button start-button" onClick={locate} disabled={locating} data-testid="locate-me-button"><Crosshair size={18} /> {locating ? "Mendeteksi lokasi…" : position ? "Perbarui lokasi saya" : "Gunakan lokasi saya"}</button>
      {error && <div className="form-error map-error" role="alert" data-testid="map-error">{error}</div>}
    </div>
    <div className="map-shell">
      <MapContainer center={center} zoom={12} scrollWheelZoom>
        <Recenter position={position} />
        <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} attribution={'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'} />
        {position && <Marker position={position} icon={userIcon}><Popup>Posisi Anda</Popup></Marker>}
        {sorted.filter(h => h.lat != null).map(h => <Marker key={h.id} position={[h.lat, h.lon]}><Popup><strong>{h.name}</strong><br />{h.address}{h.distance != null && <><br />{h.distance.toFixed(1)} km dari Anda</>}</Popup></Marker>)}
      </MapContainer>
    </div>
    <div className="hospital-list">
      {sorted.map((h, i) => <div className={`hospital-card${position && i === 0 && h.distance != null ? " nearest" : ""}`} key={h.id} data-testid={`hospital-card-${h.id}`}>
        <div className="hospital-icon"><Building2 size={19} /></div>
        <div className="hospital-info"><strong>{h.name}{position && i === 0 && h.distance != null && <span className="nearest-tag">Terdekat</span>}</strong><small>{h.address} • {h.hours || "24 jam"}</small></div>
        {h.distance != null && <span className="distance-badge">{h.distance.toFixed(1)} km • ±{Math.max(1, Math.round((h.distance / 30) * 60))} mnt</span>}
        <div className="hospital-actions">
          <a className="mini-action" href={`tel:${h.phone}`} data-testid={`hospital-call-${h.id}`}><Phone size={15} /></a>
          {h.lat != null && <a className="mini-action" target="_blank" rel="noreferrer" href={position ? `https://www.openstreetmap.org/directions?from=${position[0]}%2C${position[1]}&to=${h.lat}%2C${h.lon}` : `https://www.openstreetmap.org/?mlat=${h.lat}&mlon=${h.lon}#map=16/${h.lat}/${h.lon}`} data-testid={`hospital-route-${h.id}`}><Navigation size={15} /></a>}
        </div>
      </div>)}
      {!sorted.length && !error && <div className="empty-state">Belum ada data rumah sakit. Hubungi 112 untuk bantuan darurat.</div>}
    </div>
  </div>;
}
